import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { pad2 } from './dateCore';
import { usePopoverPosition } from './usePopoverPosition';
import { parseHhmm, parseTimeText } from './timeBoxValue';
import type { TimeBoxProps } from '../types';

/** Everything TimeBox's chrome + popover need. Returned by {@link useTimeBox}. */
export type TimeBoxController = ReturnType<typeof useTimeBox>;

/** All TimeBox state, refs and handlers; the component only wires this into the field chrome + grid. */
export function useTimeBox({
  value,
  onChange,
  step: stepProp = 5,
  minuteExpand = true,
  minCols,
  ...chrome
}: TimeBoxProps) {
  const { t } = useTranslation('dateTimeField');
  const inputId = useId();
  const step = [5, 15, 30].includes(Number(stepProp)) ? Number(stepProp) : 5;
  const cols = minCols && minCols > 0 ? minCols : step === 30 ? 2 : 4;

  const initial = parseHhmm(value);
  const [open, setOpen] = useState(false);
  const [h, setH] = useState<number | null>(initial.h);
  const [m, setM] = useState<number | null>(initial.m);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastValueRef = useRef(value);
  // Nejnovější handlery pro nativní wheel listener (viz efekt níže) — čte je až v čase události.
  const nudgeRef = useRef<(seg: 'h' | 'm', dir: 1 | -1) => void>(() => {});
  const segRef = useRef<() => 'h' | 'm'>(() => 'm');
  const popStyle = usePopoverPosition(open, anchorRef, popRef);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      const parsed = parseHhmm(value);
      setH(parsed.h);
      setM(parsed.m);
      setEditing(false);
      setInvalid(false);
      lastValueRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      setOpen(false);
      setEditing(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  // Kolečko myši nad zaostřeným inputem krokuje čas. React `onWheel` je delegovaný jako passive,
  // takže by `preventDefault()` byl no-op a stránka by se scrollovala — proto nativní non-passive
  // listener. Aktuální nudge/segAtCaret čteme přes refy, listener stačí navěsit jednou.
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const onWheel = (event: globalThis.WheelEvent) => {
      if (document.activeElement !== input) return;
      event.preventDefault();
      nudgeRef.current(segRef.current(), event.deltaY < 0 ? 1 : -1);
    };
    input.addEventListener('wheel', onWheel, { passive: false });
    return () => input.removeEventListener('wheel', onWheel);
  }, []);

  const displayText = h == null ? '' : `${pad2(h)}:${pad2(m ?? 0)}`;

  const emit = (nextH: number | null, nextM: number | null) => {
    const canonical = nextH == null ? '' : `${pad2(nextH)}:${pad2(nextM ?? 0)}`;
    if (canonical !== lastValueRef.current) {
      lastValueRef.current = canonical;
      onChange(canonical);
    }
  };

  const applyHour = (hour: number) => {
    setH(hour);
    setInvalid(false);
    setEditing(false);
    setText('');
    emit(hour, m);
  };

  const applyMinute = (minute: number) => {
    const nextH = h ?? 0;
    const shouldClose = h != null;
    setH(nextH);
    setM(minute);
    setInvalid(false);
    setEditing(false);
    setText('');
    emit(nextH, minute);
    if (shouldClose) setOpen(false);
  };

  const nudge = (seg: 'h' | 'm', dir: 1 | -1) => {
    let nextH = h ?? 8;
    let nextM = m ?? 0;
    if (seg === 'h') {
      nextH = (nextH + dir + 24) % 24;
    } else {
      nextM = Math.round(nextM / step) * step;
      nextM = (nextM + dir * step + 60) % 60;
    }
    setH(nextH);
    setM(nextM);
    setInvalid(false);
    setEditing(false);
    setText('');
    emit(nextH, nextM);
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input) input.setSelectionRange(seg === 'h' ? 0 : 3, seg === 'h' ? 2 : 5);
    });
  };

  const setNow = () => {
    const now = new Date();
    let nextM = now.getMinutes();
    if (step > 1) nextM = (Math.round(nextM / step) * step) % 60;
    const nextH = now.getHours();
    setH(nextH);
    setM(nextM);
    setInvalid(false);
    setEditing(false);
    setText('');
    emit(nextH, nextM);
  };

  const clearVal = () => {
    setH(null);
    setM(null);
    setInvalid(false);
    setEditing(false);
    setText('');
    emit(null, null);
  };

  const commit = () => {
    if (!editing) return;
    const typed = (text || '').trim();
    if (typed === '') {
      setH(null);
      setM(null);
      setEditing(false);
      setInvalid(false);
      setText('');
      emit(null, null);
      return;
    }
    if (typed === displayText) {
      setEditing(false);
      setInvalid(false);
      return;
    }
    const parsed = parseTimeText(typed);
    if (parsed === null) {
      setInvalid(true);
      setEditing(false);
      return;
    }
    if (parsed === 'empty') {
      setEditing(false);
      setInvalid(false);
      return;
    }
    setH(parsed.h);
    setM(parsed.m);
    setEditing(false);
    setInvalid(false);
    emit(parsed.h, parsed.m);
  };

  const segAtCaret = (): 'h' | 'm' => {
    const input = inputRef.current;
    if (input && input.selectionStart != null && input.selectionStart <= 2) return 'h';
    return 'm';
  };

  // Drž v refech nejnovější closurey pro nativní wheel listener (fresh h/m/step, žádná stará hodnota).
  nudgeRef.current = nudge;
  segRef.current = segAtCaret;

  const openForEdit = () => {
    setOpen(true);
    setEditing(true);
    setText(displayText);
  };

  return {
    chrome,
    inputId,
    step,
    cols,
    minuteExpand,
    gridWidth: Math.max(216, cols * (minuteExpand && step > 1 ? 46 : 32)),
    anchorRef,
    inputRef,
    popRef,
    popStyle,
    hour: h,
    minute: m,
    open,
    hasValue: h != null,
    inputText: editing ? text : displayText,
    invalid,
    errorText: invalid ? t('invalidTime') : chrome.error,
    openTimeLabel: t('openTime'),
    applyHour,
    applyMinute,
    setNow,
    clearVal,
    closePopover: () => {
      setOpen(false);
      setEditing(false);
    },
    onAffixMouseDown: (event: MouseEvent<HTMLDivElement>) => {
      const el = event.target as HTMLElement;
      if (el.closest('button')) return;
      if (el.tagName === 'INPUT') {
        if (!open) openForEdit();
        return;
      }
      event.preventDefault();
      inputRef.current?.focus();
      openForEdit();
    },
    onInputChange: (next: string) => {
      setText(next);
      setEditing(true);
      setInvalid(false);
    },
    onInputFocus: openForEdit,
    onInputClick: () => {
      if (!open) openForEdit();
    },
    onInputBlur: () => {
      commit();
      setOpen(false);
      chrome.onBlur?.();
    },
    onInputKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
        setOpen(false);
        event.currentTarget.blur();
      } else if (event.key === 'Escape') {
        // When nested inside another popover, only close our own layer on the first Escape.
        if (open) event.stopPropagation();
        setOpen(false);
        setEditing(false);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        nudge(segAtCaret(), event.key === 'ArrowUp' ? 1 : -1);
      }
    },
    onToggle: (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (open) {
        setOpen(false);
        setEditing(false);
      } else {
        openForEdit();
        inputRef.current?.focus();
      }
    },
  };
}
